/**
 * EmployeeProfilePage — /workforce/:id
 * Demo: /profile (header with badges and actions, then content with a right rail) and /manage-team (member detail).
 * Built on DetailPage. Pause, mandate editing and other lifecycle mutations are not offered here: they need the
 * workforce lifecycle endpoints. Assignment changes go through the project conversation.
 */
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AlertTriangle, ArrowRight, Check } from 'lucide-react';
import { Badge, Button, Callout, Skeleton } from '../../components/ui';
import { DetailPage } from '../../components/page/index.js';
import { PAGE_HELP } from '../../help/index.js';
import { useEmployee, useEmployeePermissionGrants, useWorkforceAssignments } from '../../hooks/workforce';
import { applyAssignmentsToView, LIFECYCLE_META, type PillTone } from './shared';
import { AllocationStrip, MandateCard, PermissionGrantsCard, ScopeNote } from './profile/RightSections.js';
import { EmployeePolicyCard } from './detail/EmployeePolicyCard.js';
import { AssignmentsHistory, CurrentFocusHero, OutputsCard, PerformanceCard } from './profile/LeftSections.js';

function ownedSince(raw: Record<string, unknown>): string | null {
  const iso = (typeof raw.hiredAt === 'string' && raw.hiredAt) || (typeof raw.createdAt === 'string' && raw.createdAt);
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
}

type BadgeTone = 'success' | 'warning' | 'danger' | 'accent' | 'purple' | 'teal' | 'neutral';

/** The bench pill vocabulary on the catalogue Badge tones. */
const TONE: Record<PillTone, BadgeTone> = {
  ok: 'success',
  warn: 'warning',
  danger: 'danger',
  accent: 'accent',
  purple: 'purple',
  teal: 'teal',
  neutral: 'neutral',
};

/** `id` shows that employee without a route (a conversation's side panel); the route's `:id` otherwise. */
export const EmployeeProfilePage: React.FC<{ id?: string }> = ({ id: idProp }) => {
  const params = useParams<{ id: string }>();
  const id = idProp ?? params.id;
  const navigate = useNavigate();
  const { data: rawEmployee, isLoading, error } = useEmployee(id);
  const { byEmployee } = useWorkforceAssignments();
  const { data: grants = [] } = useEmployeePermissionGrants(id);

  if (isLoading) {
    return (
      <div className="space-y-5" aria-busy="true">
        <Skeleton height={28} width="30%" />
        <Skeleton height={220} rounded="rounded-xl" />
      </div>
    );
  }
  if (error || !rawEmployee) {
    return (
      <DetailPage
        title="Employee"
        breadcrumbs={[{ label: 'Workforce', to: '/workforce' }]}
        alert={
          <Callout
            tone="danger"
            icon={AlertTriangle}
            title="Could not load this employee"
            actions={
              <Button size="sm" variant="secondary" onClick={() => navigate('/workforce')}>
                Back to workforce
              </Button>
            }
          >
            The profile could not be loaded. Check your connection and try again.
          </Callout>
        }
      >
        {null}
      </DetailPage>
    );
  }

  const employee = applyAssignmentsToView(rawEmployee, byEmployee.get(rawEmployee.id) ?? []);
  const raw = employee.raw as Record<string, unknown>;
  const supervisor = raw.supervisor as { name?: string } | undefined;
  const manager = supervisor?.name ?? employee.manager ?? null;
  const lifecycleMeta = LIFECYCLE_META[employee.lifecycle];
  const activeAssignments = employee.assignments.filter((a) => a.active);
  const since = ownedSince(raw);
  const templateLabel =
    (typeof raw.templateSlug === 'string' && raw.templateSlug) ||
    (typeof raw.templateName === 'string' && raw.templateName) ||
    ((raw.template as { slug?: string } | undefined)?.slug ?? null);

  const firstActive = activeAssignments[0] ?? null;
  const supervisionHref = firstActive ? `/projects/${firstActive.projectId}/workforce/${employee.id}` : '/projects';
  const supervisionLabel = firstActive ? `Open ${firstActive.projectName} supervision` : 'Open projects';

  const assignmentLabel =
    employee.load.activeAssignments === 1
      ? '1 active assignment'
      : `${employee.load.activeAssignments} active assignments`;
  const loadStateLabel =
    employee.load.state === 'assigned' ? 'assigned' : employee.load.state === 'frozen' ? 'frozen' : 'unassigned';
  const loadBadge = employee.lifecycle === 'terminated' ? '—' : `${assignmentLabel} · ${loadStateLabel}`;

  return (
    <DetailPage
      title={employee.name}
      subtitle={[
        employee.role,
        employee.tier,
        employee.kind === 'ai' ? 'AI employee' : 'Human',
        manager ? `manager: ${manager}` : null,
      ]
        .filter(Boolean)
        .join(' · ')}
      breadcrumbs={[{ label: 'Workforce', to: '/workforce' }]}
      helpContent={PAGE_HELP['workforce']}
      badges={
        <>
          <Badge variant={TONE[lifecycleMeta.pill]} dot>
            {lifecycleMeta.label}
          </Badge>
          {employee.lifecycle !== 'terminated' && (
            <Badge variant={employee.load.state === 'assigned' ? 'accent' : 'neutral'}>{loadBadge}</Badge>
          )}
          <Badge variant="neutral" icon={employee.attention.level === 'clear' ? <Check size={12} /> : undefined}>
            {employee.attention.level === 'clear' ? 'Attention clear' : employee.attention.level.replace('_', ' ')}
          </Badge>
          {since && <Badge variant="neutral">Owned since {since}</Badge>}
          <Badge variant="teal">
            {activeAssignments.length} active {activeAssignments.length === 1 ? 'project' : 'projects'}
          </Badge>
        </>
      }
      actions={
        <Button onClick={() => navigate('/projects')}>
          <ArrowRight size={14} /> Assign to project
        </Button>
      }
      meta={
        <AllocationStrip
          employee={employee}
          lifecycleLabel={lifecycleMeta.label}
          manager={manager}
          managerRole={null}
        />
      }
      rail={
        <>
          {/* The runtime policy the employee runs under, changeable here too (owner 2026-10-14). */}
          <EmployeePolicyCard employee={employee} />
          <MandateCard employee={employee} templateLabel={templateLabel} />
          <PermissionGrantsCard grants={grants} perProjectHref={supervisionHref} />
          <ScopeNote name={employee.name} supervisionHref={supervisionHref} supervisionLabel={supervisionLabel} />
        </>
      }
    >
      <CurrentFocusHero focus={employee.currentFocus} employeeId={employee.id} name={employee.name} />
      <AssignmentsHistory assignments={employee.assignments} employeeId={employee.id} name={employee.name} />
      <PerformanceCard employee={employee} />
      <OutputsCard name={employee.name} />
    </DetailPage>
  );
};
